/**
 * ManagerReviewPanel Component
 *
 * Displays all flags requiring manager attention during model planner review.
 * Groups flags by severity (critical first), shows decision UI for each flag,
 * and blocks progression until all critical decisions are made.
 */

import React from "react";
import { AlertTriangle, AlertCircle, Info, Check } from "lucide-react";

const ManagerReviewPanel = ({
  flags,
  decisions,
  onDecision,
  onContinue,
  onBack,
  canGoBack = false,
}) => {
  // Group flags by severity
  const criticalFlags = flags.filter((f) => f.severity === "critical");
  const warningFlags = flags.filter((f) => f.severity === "warning");
  const infoFlags = flags.filter((f) => f.severity === "info");

  // Check if all critical decisions are made
  const allCriticalDecided = criticalFlags.every(
    (f) => decisions[f.decisionField]
  );

  // Get severity icon
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <AlertTriangle size={16} />;
      case "warning":
        return <AlertCircle size={16} />;
      case "info":
      default:
        return <Info size={16} />;
    }
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return {
          bg: "rgba(220, 38, 38, 0.1)",
          border: "#dc2626",
          badge: "#dc2626",
          text: "#dc2626",
        };
      case "warning":
        return {
          bg: "rgba(245, 158, 11, 0.1)",
          border: "#f59e0b",
          badge: "#f59e0b",
          text: "#b45309",
        };
      case "info":
      default:
        return {
          bg: "rgba(59, 130, 246, 0.1)",
          border: "#3b82f6",
          badge: "#3b82f6",
          text: "#1d4ed8",
        };
    }
  };

  // Format section name for display
  const formatSectionName = (section) => {
    return section
      .replace(/-/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Render a single flag card
  const renderFlag = (flag) => {
    const colors = getSeverityColor(flag.severity);
    const isDecided = !!decisions[flag.decisionField];

    return (
      <div
        key={flag.decisionField}
        style={{
          backgroundColor: isDecided ? "var(--surface-tertiary)" : colors.bg,
          border: `1px solid ${isDecided ? "var(--border-secondary)" : colors.border}`,
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "12px",
          transition: "all 0.2s ease",
        }}
      >
        {/* Flag Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* Severity Badge */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 8px",
                backgroundColor: isDecided
                  ? "var(--success-bg, #22c55e)"
                  : colors.badge,
                color: "white",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: "600",
                textTransform: "uppercase",
              }}
            >
              {isDecided ? (
                <>
                  <Check size={12} /> Decided
                </>
              ) : (
                <>
                  {getSeverityIcon(flag.severity)}
                  {flag.severity}
                </>
              )}
            </span>

            {/* Section Badge */}
            <span
              style={{
                padding: "4px 8px",
                backgroundColor: "var(--surface-secondary)",
                color: "var(--text-secondary)",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: "500",
              }}
            >
              {formatSectionName(flag.section)}
            </span>

            {/* Item Name */}
            <span
              style={{
                color: "var(--text-primary)",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {flag.itemName}
            </span>
          </div>
        </div>

        {/* Flag Message */}
        <p
          style={{
            margin: "0 0 16px 0",
            color: "var(--text-primary)",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          {flag.message}
        </p>

        {/* Decision UI */}
        {flag.decisionType === "acknowledge" && (
          <button
            onClick={() => onDecision(flag.decisionField, true)}
            style={{
              padding: "8px 16px",
              fontSize: "14px",
              border: isDecided
                ? "1px solid var(--success-bg, #22c55e)"
                : "1px solid var(--border-primary)",
              backgroundColor: isDecided
                ? "var(--success-bg, #22c55e)"
                : "var(--surface-secondary)",
              color: isDecided ? "white" : "var(--text-primary)",
              cursor: "pointer",
              borderRadius: "4px",
              fontWeight: isDecided ? "600" : "400",
              transition: "all 0.15s ease",
            }}
          >
            {isDecided ? "Acknowledged" : "Acknowledge"}
          </button>
        )}

        {flag.decisionType === "choice" && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {flag.decisionOptions.map((option) => {
              const isSelected = decisions[flag.decisionField] === option;
              return (
                <button
                  key={option}
                  onClick={() => onDecision(flag.decisionField, option)}
                  style={{
                    padding: "10px 16px",
                    fontSize: "13px",
                    border: isSelected
                      ? "2px solid var(--cta-orange)"
                      : "1px solid var(--border-primary)",
                    backgroundColor: isSelected
                      ? "var(--cta-orange)"
                      : "var(--surface-secondary)",
                    color: isSelected ? "white" : "var(--text-primary)",
                    cursor: "pointer",
                    borderRadius: "6px",
                    fontWeight: isSelected ? "600" : "400",
                    transition: "all 0.15s ease",
                    flex: "1 1 auto",
                    minWidth: "150px",
                    textAlign: "center",
                  }}
                >
                  {option}
                </button>
              );
            })}
          </div>
        )}

        {/* Show current decision if made */}
        {isDecided && flag.decisionType === "choice" && (
          <div
            style={{
              marginTop: "12px",
              padding: "8px 12px",
              backgroundColor: "rgba(34, 197, 94, 0.1)",
              borderRadius: "4px",
              fontSize: "13px",
              color: "var(--text-secondary)",
            }}
          >
            <strong>Decision:</strong> {decisions[flag.decisionField]}
          </div>
        )}
      </div>
    );
  };

  // Render a flag group section
  const renderFlagGroup = (title, groupFlags, severity) => {
    if (groupFlags.length === 0) return null;

    const colors = getSeverityColor(severity);

    return (
      <div style={{ marginBottom: "32px" }}>
        <h3
          style={{
            margin: "0 0 16px 0",
            fontSize: "16px",
            fontWeight: "600",
            color: colors.text,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {getSeverityIcon(severity)}
          {title}
          <span
            style={{
              fontSize: "13px",
              fontWeight: "normal",
              color: "var(--text-secondary)",
            }}
          >
            ({groupFlags.length})
          </span>
        </h3>
        {groupFlags.map(renderFlag)}
      </div>
    );
  };

  return (
    <div className="manager-review-panel" style={{ padding: "20px" }}>
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          padding: "24px",
          borderRadius: "8px",
        }}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: "2px solid var(--border-secondary)",
          }}
        >
          <h2
            style={{
              margin: "0 0 8px 0",
              fontSize: "20px",
              color: "var(--text-primary)",
            }}
          >
            Manager Review
          </h2>
          <p
            style={{
              margin: 0,
              color: "var(--text-secondary)",
              fontSize: "14px",
            }}
          >
            Review all items below that require manager attention before
            completing the planner.
          </p>
        </div>

        {/* No Flags Message */}
        {flags.length === 0 ? (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              backgroundColor: "rgba(34, 197, 94, 0.1)",
              borderRadius: "8px",
              border: "1px solid #22c55e",
            }}
          >
            <Check
              size={48}
              style={{ color: "#22c55e", marginBottom: "12px" }}
            />
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                color: "var(--text-primary)",
              }}
            >
              No Items Require Review
            </h3>
            <p
              style={{
                margin: 0,
                color: "var(--text-secondary)",
                fontSize: "14px",
              }}
            >
              All data has been entered without flagging any conditions that
              need manager decisions.
            </p>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                marginBottom: "24px",
                flexWrap: "wrap",
              }}
            >
              {criticalFlags.length > 0 && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "rgba(220, 38, 38, 0.1)",
                    borderRadius: "6px",
                    border: "1px solid #dc2626",
                  }}
                >
                  <span style={{ fontWeight: "600", color: "#dc2626" }}>
                    {criticalFlags.length} Critical
                  </span>
                  <span style={{ color: "var(--text-secondary)", marginLeft: "8px" }}>
                    - Must decide
                  </span>
                </div>
              )}
              {warningFlags.length > 0 && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                    borderRadius: "6px",
                    border: "1px solid #f59e0b",
                  }}
                >
                  <span style={{ fontWeight: "600", color: "#b45309" }}>
                    {warningFlags.length} Warnings
                  </span>
                  <span style={{ color: "var(--text-secondary)", marginLeft: "8px" }}>
                    - Should review
                  </span>
                </div>
              )}
              {infoFlags.length > 0 && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                    borderRadius: "6px",
                    border: "1px solid #3b82f6",
                  }}
                >
                  <span style={{ fontWeight: "600", color: "#1d4ed8" }}>
                    {infoFlags.length} Info
                  </span>
                  <span style={{ color: "var(--text-secondary)", marginLeft: "8px" }}>
                    - FYI
                  </span>
                </div>
              )}
            </div>

            {/* Flag Groups */}
            {renderFlagGroup(
              "Critical Decisions Required",
              criticalFlags,
              "critical"
            )}
            {renderFlagGroup("Warnings to Review", warningFlags, "warning")}
            {renderFlagGroup("Information", infoFlags, "info")}
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          marginTop: "25px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
        }}
      >
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              color: "var(--text-primary)",
              cursor: "pointer",
              borderRadius: "6px",
            }}
          >
            Back
          </button>
        )}

        <button
          onClick={onContinue}
          disabled={!allCriticalDecided && criticalFlags.length > 0}
          style={{
            padding: "var(--space-3) var(--space-8)",
            fontSize: "var(--text-base)",
            fontWeight: "var(--font-semibold)",
            border:
              !allCriticalDecided && criticalFlags.length > 0
                ? "1px solid var(--border-primary)"
                : "1px solid var(--cta-orange)",
            backgroundColor:
              !allCriticalDecided && criticalFlags.length > 0
                ? "var(--surface-tertiary)"
                : "var(--cta-orange)",
            color:
              !allCriticalDecided && criticalFlags.length > 0
                ? "var(--text-secondary)"
                : "white",
            cursor:
              !allCriticalDecided && criticalFlags.length > 0
                ? "not-allowed"
                : "pointer",
            borderRadius: "var(--radius-md)",
            transition: "all var(--transition-fast)",
            opacity: !allCriticalDecided && criticalFlags.length > 0 ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (allCriticalDecided || criticalFlags.length === 0) {
              e.target.style.backgroundColor = "var(--cta-orange-hover)";
            }
          }}
          onMouseLeave={(e) => {
            if (allCriticalDecided || criticalFlags.length === 0) {
              e.target.style.backgroundColor = "var(--cta-orange)";
            }
          }}
        >
          {!allCriticalDecided && criticalFlags.length > 0
            ? `Complete ${criticalFlags.filter((f) => !decisions[f.decisionField]).length} Critical Decision(s) to Continue`
            : "Complete Planner"}
        </button>
      </div>
    </div>
  );
};

export default ManagerReviewPanel;
